import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { I18n } from 'utils/i18n';
import {
  useGetProvidersQuery,
  useTestProviderConfigMutation,
} from 'store/app/entities/ESignatureProviderSlice';

/** Radio values; `null` = SignRequest + Ommnio platform API key; disabled = `{ name: 'ommniosign', isActive: false }` (config may retain prior custom keys). */
const INTEGRATION_MODE = {
  PLATFORM: 'platform',
  OMMNIO: 'ommnio',
  CUSTOM: 'custom',
  DISABLED: 'disabled',
};

const markdownTooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 'min(560px, calc(100vw - 32px))',
      minWidth: 280,
      p: 0,
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: 1,
      borderColor: 'divider',
      boxShadow: 3,
      wordBreak: 'normal',
      overflowWrap: 'break-word',
      hyphens: 'none',
    },
  },
  arrow: {
    sx: { color: 'background.paper' },
  },
};

const tooltipMarkdownSx = {
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  hyphens: 'none',
};

/** Top-align with multi-line label (not centered on title+hint). Small nudge so the dot lines up with body2 cap-height. */
const integrationModeFormControlLabelSx = {
  alignItems: 'flex-start',
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
  '& .MuiRadio-root': { mt: -1 },
};

const MarkdownStrong = ({ children: c }) => (
  <Box component="strong" sx={{ fontWeight: 600 }}>
    {c}
  </Box>
);

/** Renders locale strings that use `**bold**` markdown (e.g. TSA intro) as Typography body2. */
const MarkdownBody2Text = ({ children }) => {
  const src = typeof children === 'string' ? children : '';
  if (!src) return null;
  return (
    <ReactMarkdown
      components={{
        p: ({ children: c }) => (
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: 'text.primary',
              mb: 0,
            }}
          >
            {c}
          </Typography>
        ),
        strong: MarkdownStrong,
      }}
    >
      {src}
    </ReactMarkdown>
  );
};

const TooltipMarkdownContent = ({ children }) => {
  const src = typeof children === 'string' ? children : '';
  if (!src) return null;
  return (
    <Box sx={{ p: 1.75, maxWidth: 'min(544px, calc(100vw - 48px))', ...tooltipMarkdownSx }}>
      <ReactMarkdown
        components={{
          p: ({ children: c }) => (
            <Typography
              variant="body2"
              component="p"
              sx={{
                mb: 1.25,
                lineHeight: 1.6,
                '&:last-child': { mb: 0 },
                ...tooltipMarkdownSx,
              }}
            >
              {c}
            </Typography>
          ),
          strong: ({ children: c }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {c}
            </Box>
          ),
          ul: ({ children: c }) => (
            <Box component="ul" sx={{ m: 0, mt: 0.75, mb: 1.25, pl: 2.5, listStyleType: 'disc' }}>
              {c}
            </Box>
          ),
          ol: ({ children: c }) => (
            <Box component="ol" sx={{ m: 0, mt: 0.75, mb: 1.25, pl: 2.5 }}>
              {c}
            </Box>
          ),
          li: ({ children: c }) => (
            <Box
              component="li"
              sx={{
                display: 'list-item',
                mb: 0.75,
                lineHeight: 1.6,
                typography: 'body2',
                ...tooltipMarkdownSx,
              }}
            >
              {c}
            </Box>
          ),
          code: ({ children: c }) => (
            <Box
              component="code"
              sx={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
                fontSize: '0.84em',
                bgcolor: 'action.hover',
                px: 0.5,
                py: 0.125,
                borderRadius: 0.5,
                wordBreak: 'break-all',
              }}
            >
              {c}
            </Box>
          ),
        }}
      >
        {src}
      </ReactMarkdown>
    </Box>
  );
};

const OmmnioTimestampToggleRow = ({
  checked,
  onChange,
  title,
  hint,
  tooltipMarkdown,
  helpAriaLabel,
}) => (
  <FormControlLabel
    control={
      <Switch
        size="small"
        checked={checked}
        onChange={onChange}
        slotProps={{ input: { 'aria-label': title } }}
      />
    }
    label={
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'block', cursor: 'pointer' }}>
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
          <Tooltip
            arrow
            placement="top"
            enterDelay={200}
            title={<TooltipMarkdownContent>{tooltipMarkdown}</TooltipMarkdownContent>}
            slotProps={markdownTooltipSlotProps}
          >
            <IconButton
              type="button"
              size="small"
              aria-label={helpAriaLabel}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <HelpOutlineOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 0.75,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </Typography>
      </Box>
    }
    sx={{
      alignItems: 'flex-start',
      m: 0,
      mx: 0,
      '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
    }}
  />
);

const ESignatureProviderConfig = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [testResult, setTestResult] = useState(null);
  const [timestampExpanded, setTimestampExpanded] = useState(false);

  const esp = values.eSignatureProvider;
  // `null` = SignRequest + Ommnio platform API key (no company apiKey)
  const currentProvider =
    esp == null
      ? { name: 'signrequest', config: {}, isActive: true }
      : {
          name: esp.name || 'signrequest',
          config: esp.config || {},
          isActive: Boolean(esp.isActive),
        };

  // Redux hooks for data fetching
  const { data: providers = [] } = useGetProvidersQuery();

  const [testProviderConfig, { isLoading: testLoading }] = useTestProviderConfigMutation();

  const prevCmsDts = useRef({ cms: false, dts: false });

  useEffect(() => {
    if (
      values.eSignatureProvider == null ||
      values.eSignatureProvider?.name !== 'ommniosign' ||
      !values.eSignatureProvider?.isActive
    ) {
      setTimestampExpanded(false);
      prevCmsDts.current = { cms: false, dts: false };
      return;
    }
    const cms = Boolean(values.eSignatureProvider?.config?.enableCmsArchiveTimestamp);
    const dts = Boolean(values.eSignatureProvider?.config?.enableDocumentTimestamp);
    const wasOff = !prevCmsDts.current.cms && !prevCmsDts.current.dts;
    const nowOn = cms || dts;
    if (wasOff && nowOn) {
      setTimestampExpanded(true);
    }
    prevCmsDts.current = { cms, dts };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nested eSignatureProvider fields listed intentionally
  }, [
    values.eSignatureProvider?.name,
    values.eSignatureProvider?.isActive,
    values.eSignatureProvider?.config?.enableCmsArchiveTimestamp,
    values.eSignatureProvider?.config?.enableDocumentTimestamp,
  ]);

  const externalProviderIds = useMemo(
    () => providers.filter(p => ['signrequest', 'signaturit'].includes(p)),
    [providers],
  );
  const fallbackExternalIds = useMemo(
    () => (externalProviderIds.length > 0 ? externalProviderIds : ['signrequest', 'signaturit']),
    [externalProviderIds],
  );

  useEffect(() => {
    if (values.eSignatureProvider == null) return;
    if (!values.eSignatureProvider?.isActive) return;
    if (values.eSignatureProvider?.name === 'ommniosign') return;
    const name = values.eSignatureProvider?.name;
    if (!name || fallbackExternalIds.includes(name)) return;
    setFieldValue('eSignatureProvider', {
      ...values.eSignatureProvider,
      name: fallbackExternalIds[0],
      isActive: true,
    });
  }, [values.eSignatureProvider, fallbackExternalIds, setFieldValue]);

  const showOmmnioOption =
    providers.includes('ommniosign') ||
    (currentProvider.name === 'ommniosign' && currentProvider.isActive);

  const integrationMode = (() => {
    if (values.eSignatureProvider == null) return INTEGRATION_MODE.PLATFORM;
    const e = values.eSignatureProvider;
    if ((e.isActive === false || e.isActive === 0) && e.name) return INTEGRATION_MODE.DISABLED;
    // Vacuous subdocument (no name, no own API key): same as SignRequest Ommnio / platform — not "disabled"
    if (!e.name && !e.config?.apiKey) return INTEGRATION_MODE.PLATFORM;
    if (currentProvider.name === 'ommniosign') return INTEGRATION_MODE.OMMNIO;
    return INTEGRATION_MODE.CUSTOM;
  })();

  const handleIntegrationModeChange = event => {
    const mode = event.target.value;
    if (mode === INTEGRATION_MODE.PLATFORM) {
      setFieldValue('eSignatureProvider', null);
    } else if (mode === INTEGRATION_MODE.DISABLED) {
      const prev = values.eSignatureProvider;
      setFieldValue('eSignatureProvider', {
        name: 'ommniosign',
        config: prev?.config || {},
        isActive: false,
      });
    } else if (mode === INTEGRATION_MODE.OMMNIO) {
      setFieldValue('eSignatureProvider', {
        name: 'ommniosign',
        config: {},
        isActive: true,
      });
    } else {
      const keepName =
        currentProvider.name !== 'ommniosign' && fallbackExternalIds.includes(currentProvider.name)
          ? currentProvider.name
          : fallbackExternalIds[0];
      setFieldValue('eSignatureProvider', {
        name: keepName,
        config: {},
        isActive: true,
      });
    }
    setTestResult(null);
  };

  const handleExternalProviderChange = event => {
    const newName = event.target.value;
    setFieldValue('eSignatureProvider', {
      name: newName,
      config: {},
      isActive: true,
    });
    setTestResult(null);
  };

  const handleConfigChange = (field, value) => {
    if (values.eSignatureProvider == null) return;
    setFieldValue(`eSignatureProvider.config.${field}`, value);
    setTestResult(null);
  };

  const handleTestConfiguration = async () => {
    try {
      setTestResult(null);

      const response = await testProviderConfig({
        providerName: currentProvider.name,
        config: currentProvider.config,
      }).unwrap();

      setTestResult(response);
    } catch (error) {
      // API often wraps payload as { status, data: { message, errors } }; RTK sets error.data to the full body.
      const data = error?.data;
      const nested = data?.data && typeof data.data === 'object' ? data.data : null;
      const message =
        (typeof nested?.message === 'string' && nested.message) ||
        (typeof data?.message === 'string' && data.message) ||
        I18n.t('CompanyForm.ESignatureProvider.TestError');
      const rawErrors =
        (Array.isArray(nested?.errors) && nested.errors.length > 0 && nested.errors) ||
        (Array.isArray(data?.errors) && data.errors.length > 0 && data.errors) ||
        null;
      // API often repeats the summary in errors[0]; omit bullets that duplicate the title line.
      const errorsDeduped = Array.isArray(rawErrors)
        ? rawErrors.filter(e => typeof e === 'string' && e !== message)
        : [];
      const errorList =
        errorsDeduped.length > 0
          ? errorsDeduped
          : error?.message && error.message !== message
            ? [error.message]
            : undefined;
      setTestResult({
        success: false,
        message,
        errors: errorList,
      });
    }
  };

  const providerLabel = id => {
    if (id === 'ommniosign') return 'OmmnioSign';
    if (id === 'signrequest') return 'SignRequest';
    if (id === 'signaturit') return 'Signaturit';
    return id;
  };

  const externalSelectValue = fallbackExternalIds.includes(currentProvider.name)
    ? currentProvider.name
    : fallbackExternalIds[0];

  return (
    <Grid container wrap="wrap" size={12}>
      <Grid size={12}>
        <FormControl component="fieldset" variant="standard" fullWidth sx={{ mb: 2, mx: 0 }}>
          <FormLabel component="legend" sx={{ typography: 'subtitle2', fontWeight: 600, mb: 2 }}>
            {I18n.t('CompanyForm.ESignatureProvider.IntegrationModeLabel')}
          </FormLabel>
          <RadioGroup
            name="esignature-integration-mode"
            value={integrationMode}
            onChange={handleIntegrationModeChange}
          >
            <FormControlLabel
              value={INTEGRATION_MODE.PLATFORM}
              control={<Radio size="small" />}
              label={
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {I18n.t('CompanyForm.ESignatureProvider.ModeDefault')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                    }}
                  >
                    {I18n.t('CompanyForm.ESignatureProvider.ModeDefaultHint')}
                  </Typography>
                </>
              }
              sx={{ ...integrationModeFormControlLabelSx, mb: 2 }}
            />
            {showOmmnioOption && (
              <FormControlLabel
                value={INTEGRATION_MODE.OMMNIO}
                control={<Radio size="small" />}
                label={
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} component="span">
                      {I18n.t('CompanyForm.ESignatureProvider.ModeOmmnioSign')}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                      }}
                    >
                      {I18n.t('CompanyForm.ESignatureProvider.ModeOmmnioSignHint')}
                    </Typography>
                  </>
                }
                sx={{ ...integrationModeFormControlLabelSx, mb: 2 }}
              />
            )}
            <FormControlLabel
              value={INTEGRATION_MODE.CUSTOM}
              control={<Radio size="small" />}
              slotProps={{
                label: {
                  sx: { mb: 2 },
                },
              }}
              label={
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {I18n.t('CompanyForm.ESignatureProvider.ModeCustomExternal')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                    }}
                  >
                    {I18n.t('CompanyForm.ESignatureProvider.ModeCustomExternalHint')}
                  </Typography>
                </>
              }
              sx={{ ...integrationModeFormControlLabelSx, mb: 2 }}
            />
            <FormControlLabel
              value={INTEGRATION_MODE.DISABLED}
              control={<Radio size="small" />}
              label={
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {I18n.t('CompanyForm.ESignatureProvider.ModeDisabled')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                    }}
                  >
                    {I18n.t('CompanyForm.ESignatureProvider.ModeDisabledHint')}
                  </Typography>
                </>
              }
              sx={{ ...integrationModeFormControlLabelSx, mb: 2 }}
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid sx={{ mb: 1 }} size={12}>
        <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 720 }}>
          {integrationMode === INTEGRATION_MODE.CUSTOM && (
            <FormControl fullWidth size="small">
              <InputLabel>
                {I18n.t('CompanyForm.ESignatureProvider.ExternalProviderLabel')}
              </InputLabel>
              <Select
                value={externalSelectValue}
                onChange={handleExternalProviderChange}
                label={I18n.t('CompanyForm.ESignatureProvider.ExternalProviderLabel')}
                size="small"
              >
                {fallbackExternalIds.map(id => (
                  <MenuItem key={id} value={id}>
                    {providerLabel(id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {integrationMode === INTEGRATION_MODE.OMMNIO ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel shrink>TSA Provider</InputLabel>
                <Select
                  value={currentProvider.config.tsaProvider ?? ''}
                  onChange={e => handleConfigChange('tsaProvider', e.target.value || undefined)}
                  label="TSA Provider"
                  size="small"
                  displayEmpty
                  notched
                >
                  <MenuItem value="">
                    <em>Use default</em>
                  </MenuItem>
                  <MenuItem value="camerfirma">Camerfirma</MenuItem>
                  <MenuItem value="fnmt">FNMT</MenuItem>
                  <MenuItem value="digicert" sx={{ gap: 0.5 }}>
                    DigiCert
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      — (free but not eIDAS qualified)
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Required Auth Method</InputLabel>
                <Select
                  value={currentProvider.config.requiredAuthMethod || 'any'}
                  onChange={e => handleConfigChange('requiredAuthMethod', e.target.value)}
                  label="Required Auth Method"
                  size="small"
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="webauthn">WebAuthn</MenuItem>
                  <MenuItem value="otp">OTP</MenuItem>
                </Select>
              </FormControl>

              <Accordion
                expanded={timestampExpanded}
                onChange={(_, next) => setTimestampExpanded(next)}
                elevation={0}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="ommnio-tsa-timestamp-panel"
                  id="ommnio-tsa-timestamp-header"
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    useFlexGap
                    sx={{
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      width: '100%',
                      pr: 1,
                    }}
                  >
                    <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                      <Typography variant="subtitle2" component="div">
                        {I18n.t('CompanyForm.ESignatureProvider.TsaTimestampingExpandTitle')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        {I18n.t('CompanyForm.ESignatureProvider.TsaTimestampingExpandSubtitle')}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      useFlexGap
                      sx={{
                        flexWrap: 'wrap',
                      }}
                    >
                      {Boolean(currentProvider.config.enableCmsArchiveTimestamp) && (
                        <Chip size="small" label="CMS" color="primary" variant="outlined" />
                      )}
                      {Boolean(currentProvider.config.enableDocumentTimestamp) && (
                        <Chip size="small" label="DTS" color="primary" variant="outlined" />
                      )}
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails id="ommnio-tsa-timestamp-panel" sx={{ pt: 0, px: 2, pb: 2 }}>
                  <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 1 }}>
                    <MarkdownBody2Text>
                      {I18n.t('CompanyForm.ESignatureProvider.TsaTimestampingSectionIntro')}
                    </MarkdownBody2Text>
                  </Alert>
                  <Stack spacing={2.5}>
                    <OmmnioTimestampToggleRow
                      checked={Boolean(currentProvider.config.enableCmsArchiveTimestamp)}
                      onChange={e =>
                        handleConfigChange('enableCmsArchiveTimestamp', e.target.checked)
                      }
                      title={I18n.t('CompanyForm.ESignatureProvider.EnableCms')}
                      hint={I18n.t('CompanyForm.ESignatureProvider.EnableCmsHint')}
                      tooltipMarkdown={I18n.t('CompanyForm.ESignatureProvider.EnableCmsTooltip')}
                      helpAriaLabel={I18n.t('CompanyForm.ESignatureProvider.EnableCmsHelpAria')}
                    />
                    <Divider />
                    <OmmnioTimestampToggleRow
                      checked={Boolean(currentProvider.config.enableDocumentTimestamp)}
                      onChange={e =>
                        handleConfigChange('enableDocumentTimestamp', e.target.checked)
                      }
                      title={I18n.t('CompanyForm.ESignatureProvider.EnableDts')}
                      hint={I18n.t('CompanyForm.ESignatureProvider.EnableDtsHint')}
                      tooltipMarkdown={I18n.t('CompanyForm.ESignatureProvider.EnableDtsTooltip')}
                      helpAriaLabel={I18n.t('CompanyForm.ESignatureProvider.EnableDtsHelpAria')}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          ) : integrationMode === INTEGRATION_MODE.CUSTOM ? (
            <TextField
              fullWidth
              label="API Key"
              type="password"
              required
              value={currentProvider.config.apiKey || ''}
              onChange={e => handleConfigChange('apiKey', e.target.value)}
              size="small"
            />
          ) : null}

          {integrationMode !== INTEGRATION_MODE.PLATFORM &&
            integrationMode !== INTEGRATION_MODE.DISABLED && (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={handleTestConfiguration}
                    disabled={
                      testLoading ||
                      (integrationMode === INTEGRATION_MODE.CUSTOM &&
                        !currentProvider.config.apiKey)
                    }
                  >
                    {testLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {I18n.t('CompanyForm.ESignatureProvider.Test')}
                  </Button>
                </Box>

                {testResult && (
                  <Alert severity={testResult.success ? 'success' : 'error'}>
                    <Typography variant="body2">{testResult.message}</Typography>
                    {testResult.errors && testResult.errors.length > 0 && (
                      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                        {testResult.errors.map((error, index) => (
                          <li key={index}>
                            <Typography variant="body2">{error}</Typography>
                          </li>
                        ))}
                      </Box>
                    )}
                  </Alert>
                )}
              </>
            )}
        </Stack>
      </Grid>

      {touched.eSignatureProvider && errors.eSignatureProvider && (
        <Grid size={12}>
          <Alert severity="error" sx={{ mt: 1 }}>
            {errors.eSignatureProvider}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default ESignatureProviderConfig;
